<template>
	<DataTable
		class="p-component p-shadow-2"
		:value="tableData"
		:autoLayout="true"
		:rowHover="true"
		:loading="loading"
		@row-click="onMarkRegion"
		:scrollable="true"
		scrollHeight="flex"
	>
		<template #loading>
			{{  $t('Squeeze.General.Loading') }}
		</template>
		<template #empty>
			{{  $t('Squeeze.General.NoEntriesFound',
				{ entryName: tableOrigin === 'headFields' ? $t('Squeeze.Training.Fields')
						: $t('Squeeze.Training.Region') + ' ' + $t('Squeeze.Training.Anchor') })
			}}
		</template>
		<Column field="id" header="ID" headerClass="header-center" style="width:3rem; text-align: center;" />
		<Column v-if="tableOrigin !== 'headFields'" field="description" :header="$t('Squeeze.Training.Description')" />
		<Column v-if="tableOrigin !== 'headFields'" field="keyWordPattern" :header="$t('Squeeze.Validation.General.Position') + ' ' + $t('Squeeze.Training.KeyWord')" />
		<Column v-if="tableOrigin === 'headFields'" field="fieldId" :header="$t('Squeeze.DocumentClasses.Field')">
			<template #body="slotProps">
				<div>
					{{ getFieldName(slotProps.data.fieldId) }}
				</div>
			</template>
		</Column>
		<Column v-if="tableOrigin === 'headFields'" field="valuePattern" :header="$t('Squeeze.Training.KeyWord')" />
		<Column field="locatorId" :header="$t('Squeeze.Training.Locator') + ' ID'" headerClass="header-center" style="width:3rem; text-align: center;" />
		<Column v-if="tableOrigin !== 'headFields'" field="ignoreWhiteSpaces" :header="$t('Squeeze.Training.IgnoreSpaces')">
			<template #body="slotProps">
				<div v-if="JSON.stringify(slotProps.data.ignoreWhiteSpaces) === 'true' || JSON.stringify(slotProps.data.ignoreWhiteSpaces) === 'false'">
					{{ $t("Squeeze.General.Boolean." + JSON.stringify(slotProps.data.ignoreWhiteSpaces)) }}
				</div>
				<div v-else>
					{{ slotProps.data.ignoreWhiteSpaces }}
				</div>
			</template>
		</Column>
		<Column v-if="tableOrigin === 'headFields'" field="valueIgnoreWhitespace" :header="$t('Squeeze.Training.IgnoreSpaces')">
			<template #body="slotProps">
				<div v-if="JSON.stringify(slotProps.data.valueIgnoreWhitespace) === 'true' || JSON.stringify(slotProps.data.valueIgnoreWhitespace) === 'false'">
					{{ $t("Squeeze.General.Boolean." + JSON.stringify(slotProps.data.valueIgnoreWhitespace)) }}
				</div>
				<div v-else>
					{{ slotProps.data.valueIgnoreWhitespace }}
				</div>
			</template>
		</Column>
		<Column v-if="tableOrigin === 'headFields'" field="accumulateFoundValues" :header="$t('Squeeze.Training.MultilineReadout')">
			<template #body="slotProps">
				<div v-if="JSON.stringify(slotProps.data.accumulateFoundValues) === 'true' || JSON.stringify(slotProps.data.accumulateFoundValues) === 'false'">
					{{ $t("Squeeze.General.Boolean." + JSON.stringify(slotProps.data.accumulateFoundValues)) }}
				</div>
				<div v-else>
					{{ slotProps.data.accumulateFoundValues }}
				</div>
			</template>
		</Column>
		<Column style="min-width: 8rem; text-align: right;">
			<template #body="slotProps">
				<Button v-tooltip="$t('Squeeze.Locators.Edit')" icon="mdi mdi-pencil-outline" class="p-button p-component p-button-icon-only p-button-lg p-button-rounded p-button-success p-button-text" @click="changeData(slotProps.data)"/>
				<Button v-tooltip="$t('Squeeze.Validation.Buttons.Delete')" icon="mdi mdi-delete-outline" class="p-button p-button-rounded p-button-danger p-button-text deleteButtonRow" @click="deleteData(slotProps.data)"/>
			</template>
		</Column>
	</DataTable>
</template>

<script lang="ts">
import {getTranslatedDescription} from "@/util/TranslationHelper";
import {defineComponent, PropType} from "vue";
import {useI18n} from "vue-i18n";
import Button from "primevue/button";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import {DocumentField} from "@dex/squeeze-client-ts";

export default defineComponent({
	name: "FormHeadTrainingTable",
	components: {
		Button,
		DataTable,
		Column,
	},
	props: {
		tableOrigin: {
			type: String,
			default: '',
		},
		tableData: {
			type: Array as PropType<any[]>,
			default: () => [],
		},
		loading: {
			type: Boolean,
			default: false,
		},
		documentFields: {
			type: Array as PropType<DocumentField[]>,
			default: () => [],
		},
	},
	emits: [
		'changeData',
		'deleteData',
		'onMarkRegion',
	],
	setup(props, {emit}) {
		const {t} = useI18n();

		/**
		 * Change an indicator
		 * @param rowData
		 */
		const changeData = (rowData: any) => {
			emit("changeData", rowData);
		}

		/**
		 * Deletes an indicator
		 * @param rowData
		 */
		const deleteData = (rowData: any) => {
			emit("deleteData", rowData);
		}

		/**
		 * Click a row to mark
		 * @param event
		 */
		const onMarkRegion = (event: any) => {
			emit("onMarkRegion", event);
		}

		/**
		 * Get field name
		 * @param fieldId
		 */
		const getFieldName = (fieldId: number) => {
			const field = props.documentFields.find(field => field.id === fieldId);
			return field ? getTranslatedDescription(field) : fieldId;
		}

		return {
			t,
			changeData,
			deleteData,
			onMarkRegion,
			getFieldName,
		}
	},
});
</script>

<style scoped>

::v-deep(.header-center div.p-column-header-content) {
	justify-content: center;
}

</style>
